import * as Dialog from '@radix-ui/react-dialog';
import { Download, ExternalLink, X } from 'lucide-react';
import { Button } from '../ui/Button';
import type { TicketAttachment } from '../../services/tickets/ticketService';

const OFFICE_VIEWER_EMBED = 'https://view.officeapps.live.com/op/embed.aspx?src=';
const OFFICE_VIEWER_OPEN = 'https://view.officeapps.live.com/op/view.aspx?src=';

const WORD_MIMES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const EXCEL_MIMES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

interface TicketAttachmentPreviewProps {
  attachment: TicketAttachment | null;
  onOpenChange: (open: boolean) => void;
}

export function TicketAttachmentPreview({
  attachment,
  onOpenChange,
}: TicketAttachmentPreviewProps) {
  if (!attachment) return null;
  const att = attachment;

  const url = att.signed_url ?? '';
  const mime = att.mime_type;
  const isPdf = mime === 'application/pdf';
  const isImage = mime.startsWith('image/');
  const isWord = WORD_MIMES.includes(mime);
  const isExcel = EXCEL_MIMES.includes(mime);

  const officeEmbedUrl = isWord ? `${OFFICE_VIEWER_EMBED}${encodeURIComponent(url)}` : '';
  const officeOpenUrl = isExcel ? `${OFFICE_VIEWER_OPEN}${encodeURIComponent(url)}` : '';

  function renderBody() {
    if (!url) {
      return (
        <div className="flex items-center justify-center h-full text-neutral-400 text-sm">
          URL signée indisponible.
        </div>
      );
    }
    if (isPdf) {
      return <iframe src={url} className="w-full h-full border-0" title={att.filename} />;
    }
    if (isImage) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-neutral-900/5 overflow-auto">
          <img
            src={url}
            alt={att.filename}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }
    if (isWord) {
      return (
        <iframe
          src={officeEmbedUrl}
          className="w-full h-full border-0"
          title={att.filename}
        />
      );
    }
    if (isExcel) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
          <p className="text-sm text-neutral-600">
            Les fichiers Excel s'ouvrent dans un nouvel onglet via Excel Online.
          </p>
          <Button onClick={() => window.open(officeOpenUrl, '_blank', 'noopener,noreferrer')}>
            <ExternalLink className="w-4 h-4" />
            Ouvrir dans Excel Online
          </Button>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
        <p className="text-sm text-neutral-600">
          Aperçu indisponible pour ce type de fichier.
        </p>
        <Button onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
          <Download className="w-4 h-4" />
          Télécharger
        </Button>
      </div>
    );
  }

  return (
    <Dialog.Root open={!!attachment} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
        <Dialog.Content className="fixed inset-4 md:inset-10 z-50 bg-white rounded-cime shadow-xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-100">
            <Dialog.Title className="font-heading text-lg font-semibold text-neutral-800 truncate pr-4">
              {att.filename}
            </Dialog.Title>
            <div className="flex items-center gap-2">
              {url && (
                <button
                  onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                  title="Ouvrir dans un nouvel onglet"
                  className="p-1.5 rounded text-neutral-500 hover:text-cime-primary hover:bg-neutral-100 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}
              <Dialog.Close asChild>
                <button
                  title="Fermer"
                  className="p-1.5 rounded text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </Dialog.Close>
            </div>
          </div>
          <div className="flex-1 min-h-0 bg-white">{renderBody()}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
